import React from 'react';
import { View, Text, type ViewStyle, type TextStyle } from 'react-native';
import { cn } from '@/lib/cn';
import { useThemeColors } from '@/lib/useThemeColors';
import type { LucideIcon } from 'lucide-react-native';

type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral';
type BadgeSize = 'sm' | 'md';

interface StatusBadgeProps {
  /** The label text to display */
  label: string;
  /** Semantic variant for auto-coloring */
  variant?: BadgeVariant;
  /** Size of the badge (default: 'sm') */
  size?: BadgeSize;
  /** Override the text color */
  color?: string;
  /** Override the background color */
  backgroundColor?: string;
  /** Optional icon shown before the label */
  icon?: LucideIcon;
  /** Whether the icon should be filled */
  iconFilled?: boolean;
  /**
   * Outlined "tag" treatment: transparent fill + colored border, so an
   * informational tag reads distinctly from a solid status/action badge.
   */
  outlined?: boolean;
  /** Additional className for the outer container */
  className?: string;
  /** Additional style for the outer container */
  style?: ViewStyle;
}

/**
 * Colored pill badge for displaying statuses, tags, and labels.
 * Extracted from the ubiquitous `px-2 py-0.5 rounded-full` pattern
 * used for deal status, portfolio status, role badges, etc.
 *
 * Usage:
 * ```tsx
 * <StatusBadge label="Active" variant="success" />
 * <StatusBadge label="At Risk" variant="error" icon={AlertCircle} />
 * <StatusBadge label="Custom" color="#6366f1" backgroundColor="#eef2ff" />
 * ```
 */
export function StatusBadge({
  label,
  variant = 'neutral',
  size = 'sm',
  color,
  backgroundColor,
  icon: Icon,
  iconFilled = false,
  outlined = false,
  className,
  style,
}: StatusBadgeProps) {
  const colors = useThemeColors();

  const variantStyles: Record<BadgeVariant, { color: string; bg: string }> = {
    success: { color: colors.success, bg: colors.traffic.greenLight },
    warning: { color: colors.warning, bg: colors.amber.light },
    error: { color: colors.error, bg: colors.red.light },
    info: { color: colors.primary[600], bg: colors.primary[50] },
    neutral: { color: colors.text.tertiary, bg: colors.light[300] },
  };

  const resolvedColor = color ?? variantStyles[variant].color;
  const resolvedBg = backgroundColor ?? variantStyles[variant].bg;

  const sizeStyles: Record<BadgeSize, { container: string; text: string; iconSize: number }> = {
    sm: { container: 'px-2 py-0.5', text: 'text-xs', iconSize: 12 },
    md: { container: 'px-3 py-1', text: 'text-sm', iconSize: 14 },
  };

  const sizeConfig = sizeStyles[size];

  const containerStyle: ViewStyle = {
    backgroundColor: outlined ? 'transparent' : resolvedBg,
    ...(outlined ? { borderWidth: 1, borderColor: resolvedColor } : {}),
    ...style,
  };

  const textStyle: TextStyle = {
    color: resolvedColor,
  };

  return (
    <View
      className={cn(
        'rounded-full flex-row items-center',
        sizeConfig.container,
        Icon ? 'gap-1' : '',
        className,
      )}
      style={containerStyle}
      accessibilityRole="text"
      accessibilityLabel={label}
    >
      {Icon && (
        <Icon
          size={sizeConfig.iconSize}
          color={resolvedColor}
          {...(iconFilled ? { fill: resolvedColor } : {})}
        />
      )}
      <Text className={cn('font-medium', sizeConfig.text)} style={textStyle}>
        {label}
      </Text>
    </View>
  );
}
